import React, { useMemo, useState, useRef, useEffect } from "react";
import dictionaryIcon from "../../../assets/icons/dictionary.svg";

function DictationPhrasesSettingsMenu({
  title,
  description,
  inputLabel,
  inputPlaceholder,
  addButtonLabel,
  onAddPhrases,
  settingsAriaLabel,
  successText,
}) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open]);

  const phrases = useMemo(() => {
    const raw = value
      .split(/\r?\n|,/g)
      .map((s) => s.trim())
      .filter(Boolean);
    return Array.from(new Set(raw));
  }, [value]);

  const handleAdd = async () => {
    if (!phrases.length || isSaving) return;
    try {
      setIsSaving(true);
      await onAddPhrases(phrases);
      setValue("");
      setOpen(false);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="relative flex-shrink-0" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={settingsAriaLabel}
        title={settingsAriaLabel}
        className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 flex items-center justify-center focus:outline-none"
      >
        <img
          src={dictionaryIcon}
          alt=""
          aria-hidden
          className="w-3.5 h-3.5 sm:w-4 sm:h-4 dark:invert"
        />
      </button>

      {open && (
        <div
          role="dialog"
          aria-label={title}
          className="absolute end-0 z-[100] mt-1.5 w-[min(calc(100vw-2rem),260px)] rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-lg p-3 text-start"
        >
          <p className="text-xs font-semibold text-gray-900 dark:text-gray-100">
            {title}
          </p>
          <p className="text-[11px] sm:text-xs text-gray-600 dark:text-gray-400 mt-1 leading-snug">
            {description}
          </p>
          <label className="mt-2 block text-[11px] sm:text-xs font-medium text-gray-700 dark:text-gray-300">
            {inputLabel}
          </label>
          <textarea
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={inputPlaceholder}
            rows={3}
            className="mt-1 w-full rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 p-2 text-xs text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none"
          />
          {successText && (
            <p className="mt-1 text-[10px] sm:text-[11px] text-gray-500 dark:text-gray-400">
              {successText}
            </p>
          )}
          <button
            type="button"
            onClick={handleAdd}
            disabled={!phrases.length || isSaving}
            className="mt-3 w-full h-9 rounded-full text-xs font-medium border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-60 disabled:cursor-not-allowed text-gray-900 dark:text-gray-100"
          >
            {isSaving ? "..." : addButtonLabel}
          </button>
        </div>
      )}
    </div>
  );
}

export default DictationPhrasesSettingsMenu;
